/**
 * Admin Types
 * TypeScript definitions for admin authentication and management
 */

export type AdminRole = 'owner' | 'manager' | 'staff';

export interface Admin {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  super_admin?: boolean;
  selected_restaurant_id?: string;
  selected_restaurant_name?: string;
  restaurants?: Array<{ id: string; name: string }>;
  created_at?: string;
  updated_at?: string;
}

export interface AdminAuthResponse {
  message: string;
  admin: Admin;
}

export interface AdminLoginRequest {
  admin: {
    email: string;
    password: string;
  };
}

export interface AdminCreateRequest {
  admin: {
    email: string;
    password: string;
    password_confirmation: string;
    name: string;
    role: AdminRole;
  };
}

export interface AdminUpdateRequest {
  admin: {
    email?: string;
    password?: string;
    password_confirmation?: string;
    current_password?: string;
    name?: string;
  };
}

export interface DashboardStats {
  memories_today: number;
  active_orders: number;
  total_items: number;
  total_admins: number;
  memories_this_week?: number;
  memories_this_month?: number;
}

// Role permissions helpers
export const AdminPermissions = {
  canManageAdmins: (role: AdminRole) => role === 'owner',
  canEditRestaurant: (role: AdminRole) => role === 'owner' || role === 'manager',
  canViewDesk: (_role: AdminRole) => true, // eslint-disable-line @typescript-eslint/no-unused-vars
  canManageItems: (role: AdminRole) => role === 'owner' || role === 'manager',
} as const;

// Role display names
export const AdminRoleLabels: Record<AdminRole, string> = {
  owner: 'Propietario',
  manager: 'Gerente',
  staff: 'Personal',
} as const;

// ============================================================================
// CLIENTS DASHBOARD TYPES
// ============================================================================

// Pagination metadata
export interface PaginationMeta {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Client types
export interface Client {
  id: string;
  email: string;
  name: string | null;
  first_visit: string;
  last_visit: string;
  memories_count: number;
}

export interface ClientsListResponse {
  clients: Client[];
  meta: PaginationMeta;
}

export interface Companion {
  id: string;
  name: string | null;
  email: string;
}

export interface MemoryDetail {
  id: string;
  slug: string;
  created_at: string;
  status: string;
  order: {
    provider_order_id: string;
    guests_count: number;
    total_amount: {
      amount: number;
      currency: string;
    };
    companions: Companion[];
  };
  video_status: 'generated' | 'processing' | 'failed' | 'pending';
  video_views_count: number;
  last_viewed_at: string | null;
}

export interface ClientDetail {
  client: {
    id: string;
    email: string;
    name: string | null;
    created_at: string;
  };
  memories: MemoryDetail[];
}

// Filter and sort types
export type ClientFilter = 'all';

// ============================================================================
// SUPERADMIN USER MANAGEMENT TYPES
// ============================================================================

export interface AdminWithRestaurants extends Admin {
  restaurants: Array<{ id: string; name: string }>;
}

export interface AdminListResponse {
  admins: AdminWithRestaurants[];
}

export interface CreateAdminRequest {
  admin: {
    email: string;
    password: string;
    name: string;
    role: AdminRole;
    super_admin?: boolean;
  };
  restaurant_ids?: string[];
}

export interface UpdateAdminRequest {
  admin: {
    email?: string;
    password?: string;
    name?: string;
    role?: AdminRole;
    super_admin?: boolean;
  };
  restaurant_ids?: string[];
}
